import React, { useEffect, useMemo, useRef, useState } from "react";

export interface SearchSelectOption {
    label: string;
    value: string | number;
    [key: string]: unknown;
}

export interface SearchSelectProps {
    id?: string;
    label?: string;
    options: SearchSelectOption[];
    value?: string | number | null;
    onChange?: (option: SearchSelectOption | null) => void;
    placeholder?: string;
    disabled?: boolean;
    loading?: boolean;
    required?: boolean;
    allowClear?: boolean;
    onClear?: () => void;
    className?: string;
}

const SearchSelect: React.FC<SearchSelectProps> = ({
    id,
    label,
    options,
    value,
    onChange,
    placeholder = "Select...",
    disabled = false,
    loading = false,
    required = false,
    allowClear = false,
    onClear,
    className = "",
}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const containerRef = useRef<HTMLDivElement>(null);

    const normalizedValue = value === undefined || value === null ? "" : String(value);

    const selectedOption = useMemo(
        () =>
            options.find((option) => String(option.value) === normalizedValue) || null,
        [normalizedValue, options]
    );

    const filteredOptions = useMemo(() => {
        if (!search.trim()) {
            return options;
        }

        const term = search.trim().toLowerCase();
        return options.filter((option) =>
            option.label?.toLowerCase().includes(term)
        );
    }, [options, search]);

    useEffect(() => {
        if (!open) {
            setSearch("");
            return undefined;
        }

        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [open]);

    const toggleOpen = () => {
        if (disabled) {
            return;
        }
        setOpen((prev) => !prev);
    };

    const handleSelect = (option: SearchSelectOption | null) => {
        if (onChange) {
            onChange(option || null);
        }
        if (!option && onClear) {
            onClear();
        }
        setOpen(false);
    };

    return (
        <div ref={containerRef} className={`relative ${className}`}>
            {label && (
                <label htmlFor={id} className="block text-sm font-medium text-[#475467] mb-2">
                    {label}
                    {required && <span className="text-red-500">*</span>}
                </label>
            )}

            <button
                id={id}
                type="button"
                onClick={toggleOpen}
                disabled={disabled}
                className={`w-full border border-[#D0D5DD] rounded-lg px-4 py-3 text-left flex items-center justify-between gap-3 focus:ring-2 focus:ring-[#317333]/20 focus:border-[#317333] outline-none transition ${disabled ? "bg-[#F9FAFB] text-[#98A2B3] cursor-not-allowed" : "bg-white text-[#101828]"
                    }`}
            >
                <span className={selectedOption ? "" : "text-[#98A2B3]"}>
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <svg
                    className={`h-4 w-4 text-[#667085] transition-transform ${open ? "rotate-180" : ""}`}
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    aria-hidden="true"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                </svg>
            </button>

            {open && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-[#EAECF0] rounded-lg shadow-lg">
                    <div className="p-2 border-b border-[#EAECF0]">
                        <input
                            type="text"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder="Search..."
                            className="w-full border border-[#D0D5DD] rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#317333]/20 focus:border-[#317333] outline-none"
                            autoFocus
                        />
                    </div>
                    <div className="max-h-56 overflow-y-auto">
                        {allowClear && normalizedValue && (
                            <button
                                type="button"
                                onClick={() => handleSelect(null)}
                                className="w-full text-left px-3 py-2 text-sm text-[#B42318] hover:bg-[#FEF3F2]"
                            >
                                Clear selection
                            </button>
                        )}
                        {(() => {
                            if (loading) return <p className="px-3 py-2 text-sm text-[#475467]">Loading...</p>;
                            if (filteredOptions.length === 0) return <p className="px-3 py-2 text-sm text-[#475467]">No matches found</p>;

                            return filteredOptions.map((option) => {
                                const isSelected = selectedOption?.value === option.value;
                                return (
                                    <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => handleSelect(option)}
                                        className={`w-full text-left px-3 py-2 text-sm transition ${isSelected
                                            ? "bg-[#EEF4FF] text-[#175CD3]"
                                            : "text-[#101828] hover:bg-[#F2F4F7]"
                                            }`}
                                    >
                                        {option.label}
                                    </button>
                                );
                            });
                        })()}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchSelect;
